import { useEffect, useRef, useState } from 'react';
import MUITextField from '@mui/material/TextField';
import ClearIcon from '@mui/icons-material/Clear';
import {
  Select,
  MenuItem,
  Box,
  Checkbox,
  FormLabel,
  Divider,
  Pagination,
  InputAdornment,
  IconButton,
  useTheme,
  Tooltip,
  Typography,
} from '@mui/material';
import { FormattedMessage } from 'react-intl';
import { MAP_SP_FILTER } from '@constants/servicejob';
import { SkeletonList, useLocalization } from '@ui-framework/index';
import Grid2 from '@thirdparty-framework/mui/material/Grid';
import MapLib from '@product-framework/pages/ServiceJob/components/Map/lib/MapLib';
import ServiceProviderWrapper from '../../markers/ServiceProvider/ServiceProviderWrapper';
import ServiceProviderName from './ServiceProviderName';

const getDispatchEventStatusCounts = response => {
  const counts = response.reduce((acc, item) => {
    const status = item?.dispatchEvent?.status;

    if (status) {
      acc[status] = (acc[status] || 0) + 1;
    }

    return acc;
  }, {});

  const result = Object.entries(counts).map(([label, value]) => {
    return {
      value: label,
      count: value,
    };
  });

  return [
    {
      value: 'ALL',
    },
    ...result,
  ];
};

const SEARCH_DEBOUNCE_MS = 500;

export default function Filter({
  spLoading,
  mapRef,
  serviceProviders,
  setSearchText,
  setDispatchEventStatus,
  setPagination,
  searchText,
  dispatchEventStatus,
  pagination,
  dispatchStatusConfig,
  sortOption,
  setSortOption,
  dispatchStatusDynamic = false,
  onServiceProviderClick = () => undefined,
  isAssignedOrAcceptedSP = () => false,
  closeServiceProviderPopover,
  jobInfo,
  sortOptions,
}) {
  const theme = useTheme();
  const { formatMessage } = useLocalization();

  const dispatchStatusesForDropDown = dispatchStatusDynamic
    ? getDispatchEventStatusCounts(serviceProviders, formatMessage)
    : dispatchStatusConfig;

  const lastFilterList = useRef(dispatchStatusesForDropDown || []);
  const [checkedProviders, setCheckedProviders] = useState({});
  const [searchInputValue, setSearchInputValue] = useState(searchText);
  const serviceProviderIconRefs = useRef({});

  useEffect(() => {
    setSearchInputValue(searchText);
  }, [searchText]);

  useEffect(() => {
    // Debouce logic
    const timeoutId = window.setTimeout(() => {
      if (
        (searchInputValue !== searchText &&
          searchInputValue?.trim().length > 1) ||
        searchInputValue?.trim().length === 0
      ) {
        setSearchText(searchInputValue);
      }
    }, SEARCH_DEBOUNCE_MS);

    return () => window.clearTimeout(timeoutId);
  }, [searchInputValue, searchText, setSearchText]);

  const getPageCount = (total, limit) => Math.ceil(total / limit);

  const pageCount = getPageCount(pagination?.total, pagination?.limit);

  useEffect(() => {
    setCheckedProviders(prev => {
      const updated = {};
      serviceProviders.forEach(sp => {
        updated[sp.id] = prev[sp.id] ?? true;
      });
      return updated;
    });
  }, [serviceProviders]);

  const allChecked =
    serviceProviders.length > 0 &&
    serviceProviders.every(sp => checkedProviders[sp.id]);
  const someChecked =
    serviceProviders.length > 0 &&
    serviceProviders.some(sp => checkedProviders[sp.id]) &&
    !allChecked;

  const gotToServiceJobLocation = () => {
    mapRef.current.jumpTo(jobInfo?.locationLat, jobInfo?.locationLng);
  };

  const handleCheck = (checked, item, index) => {
    MapLib.serviceProviderToggleMarker(item?.id, item?.locations, checked);
    mapRef.current.serviceProviderServiceJobToggleMarker(checked, index);

    if (isAssignedOrAcceptedSP(item)) {
      if (!checked) {
        mapRef.current.hideAssignedRoute();
      } else {
        mapRef.current.showAssignedRoute();
      }
    }
  };

  const handleCheckAll = checked => {
    const updated = {};
    serviceProviders.forEach((sp, index) => {
      updated[sp.id] = checked;
      handleCheck(checked, sp, index);
    });
    setCheckedProviders(updated);
    gotToServiceJobLocation();
  };

  const handleIndividualCheck = (checked, item, index) => {
    setCheckedProviders(prev => ({ ...prev, [item.id]: checked }));
    handleCheck(checked, item, index);
  };

  const onServiceProviderClicked = ({ e, sp, location }) => {
    onServiceProviderClick({ e, sp, location });
  };

  return (
    <Box sx={{ px: 0.5 }}>
      <Grid2 container spacing={0.3}>
        <Grid2 container xs={12} spacing={1}>
          <Grid2 xs={12} md={6}>
            <Tooltip
              title="Minimum 2 characters are needed"
              open={searchInputValue.length === 1}
              arrow>
              <MUITextField
                fullWidth
                placeholder={formatMessage({
                  id: 'SEARCH_BY_NAME',
                  defaultMessage: 'Search by Name',
                })}
                size="small"
                value={searchInputValue}
                onChange={e => {
                  setSearchInputValue(e.target?.value);
                }}
                InputProps={{
                  endAdornment: searchInputValue && (
                    <InputAdornment position="end">
                      <IconButton
                        aria-label="clear text"
                        onClick={() => {
                          setSearchInputValue('');
                          setSearchText('');
                        }}
                        edge="end"
                        size="small">
                        <ClearIcon fontSize="small" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Tooltip>
          </Grid2>
          <Grid2 xs={12} md={6}>
            <Select
              sx={{ mt: -0.5 }}
              defaultValue="ALL"
              fullWidth
              name="dispatchStatus"
              size="small"
              value={dispatchEventStatus}
              onChange={e => {
                setDispatchEventStatus(e.target?.value);
                gotToServiceJobLocation();
                handleCheckAll(true);
                closeServiceProviderPopover();
              }}>
              {lastFilterList.current.map(option => (
                <MenuItem key={option.value} value={option.value}>
                  <FormattedMessage
                    id={option.value}
                    defaultMessage={option.value}
                  />
                  {option?.count && <span> ({option.count})</span>}
                </MenuItem>
              ))}
            </Select>
          </Grid2>
        </Grid2>
        <Grid2 xs={12}>
          <Divider sx={{ mt: 1, mb: 1 }} />
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
            }}>
            <Typography sx={{ fontWeight: 'bold' }} variant="body2">
              <FormattedMessage
                id="DISPLAYING_RECORDS"
                defaultMessage="Displaying {count} records"
                values={{ count: serviceProviders?.length }}
              />
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Box>
                <Typography
                  color="text.secondary"
                  variant="subtitle2"
                  sx={{ lineHeight: '1.1em' }}
                  component="p">
                  {formatMessage({
                    id: 'SORT_BY',
                    defaultMessage: 'Sort By',
                  })}
                </Typography>
                <Select
                  sx={{
                    minWidth: 180,
                    borderRadius: '4px',
                    '& .MuiSelect-select': {
                      py: 0.5, // reduce vertical padding
                      lineHeight: 1,
                      display: 'flex',
                      alignItems: 'center',
                    },
                  }}
                  size="small"
                  value={sortOption}
                  onChange={e => setSortOption(e.target.value)}>
                  {sortOptions.map(option => (
                    <MenuItem key={option.value} value={option.value}>
                      {option.label}
                    </MenuItem>
                  ))}
                </Select>
              </Box>
              <Checkbox
                size="small"
                checked={allChecked}
                indeterminate={someChecked}
                onChange={e => handleCheckAll(e.target.checked)}
              />
            </Box>
          </Box>
          <Divider sx={{ mt: 1 }} />
        </Grid2>
        {spLoading && (
          <Grid2 xs={12}>
            <SkeletonList>processing...</SkeletonList>
          </Grid2>
        )}
        {!spLoading && (
          <Grid2
            xs={12}
            sx={{
              maxHeight: window.innerHeight - 420,
              overflow: 'scroll',
              mt: 1,
            }}>
            {serviceProviders
              .filter(
                sp =>
                  dispatchEventStatus === 'ALL' ||
                  sp?.dispatchEvent?.status === dispatchEventStatus,
              )
              .map((sp, index) => (
                <Box key={sp.id} sx={{ marginBottom: '3px' }}>
                  {index !== 0 && <Divider sx={{ m: 0.5 }} />}
                  <Grid2 container spacing={0.5} alignItems="center">
                    <Grid2
                      sx={{ display: 'flex', alignItems: 'center' }}
                      ref={el => {
                        if (el) {
                          serviceProviderIconRefs.current[sp.id] = el;
                        } else {
                          delete serviceProviderIconRefs.current[sp.id];
                        }
                      }}>
                      <ServiceProviderWrapper
                        disabled={!checkedProviders[sp.id]}
                        MapLib={MapLib}
                        theme={theme}
                        serviceProvider={sp}
                        size="small"
                        onClickHandler={({ e, location }) => {
                          if (!checkedProviders[sp.id]) {
                            return;
                          }
                          onServiceProviderClicked({
                            e,
                            sp,
                            location,
                          });
                        }}
                        index={index}
                        map={null}
                        // Different style for assigned or accepted
                        type={
                          isAssignedOrAcceptedSP(sp)
                            ? 'ASSIGNED_OR_ACCEPTED'
                            : 'GENERIC'
                        }
                        nonGenericTypeHidePointer
                        location={sp.locations[0]}
                      />
                    </Grid2>
                    <Grid2 xs={8}>
                      <ServiceProviderName
                        sp={sp}
                        disabled={!checkedProviders[sp.id]}
                        location={sp.locations?.[0]}
                        mapRef={mapRef}
                        onClickHandler={e => {
                          if (!checkedProviders[sp.id]) {
                            return;
                          }
                          const root = serviceProviderIconRefs.current[sp.id];
                          const markerSvg =
                            root?.querySelector?.('svg.map_clickable') ??
                            root?.querySelector?.('svg');
                          if (!markerSvg?.getAttribute) return;
                          onServiceProviderClicked({
                            e,
                            sp,
                            location: sp.locations[0],
                          });
                        }}
                      />
                    </Grid2>
                    <Grid2 xs="grow" textAlign="right">
                      <Checkbox
                        size="small"
                        checked={checkedProviders[sp.id] ?? true}
                        name="serviceProvider"
                        value={sp.name}
                        onChange={e =>
                          handleIndividualCheck(e.target.checked, sp, index)
                        }
                      />
                    </Grid2>
                  </Grid2>
                </Box>
              ))}
            {serviceProviders.length === 0 && (
              <FormLabel sx={{ color: theme.palette.error.main }}>
                No Service Providers Found
              </FormLabel>
            )}
            <Box>
              {MAP_SP_FILTER?.pagination && !spLoading && pageCount > 1 && (
                <Pagination
                  page={pagination?.page}
                  onChange={async (event, value) => {
                    setPagination({
                      ...pagination,
                      page: value,
                    });
                  }}
                  sx={{
                    mt: 1,
                    mr: 1,
                    ml: 1,
                    justifyContent: 'flex-end',
                    display: 'flex',
                  }}
                  count={pageCount}
                  size="small"
                />
              )}
            </Box>
          </Grid2>
        )}
      </Grid2>
    </Box>
  );
}
